<template>
  <div class="restaurante-producao">
    <div class="card card-custom gutter-b">
      <div class="card-header border-0 py-5">
        <h3 class="card-title align-items-start flex-column">
          <span class="card-label font-weight-bolder text-dark">
            {{ titulo }}
          </span>
          <span class="text-muted mt-2 font-weight-bold font-size-sm">
            {{ subtitulo }}
          </span>
        </h3>
        <div class="card-toolbar">
          <router-link
            v-if="exibirMesasAtalho"
            class="btn btn-light-dark font-weight-bolder font-size-sm mr-2"
            :to="{ name: 'restauranteMesas' }"
          >
            Mesas
          </router-link>
          <button
            class="btn btn-dark font-weight-bolder font-size-sm"
            @click="getPainel"
          >
            Atualizar
          </button>
        </div>
      </div>

      <div class="card-body pt-0">
        <div class="kds-resumo">
          <div
            v-for="metric in producaoMetrics"
            :key="metric.label"
            class="kds-metric"
          >
            <span>{{ metric.label }}</span>
            <strong>{{ metric.value }}</strong>
          </div>
        </div>

        <b-row class="align-items-end mb-6">
          <b-col :md="restauranteProducaoEtapaUnica ? 6 : 4">
            <b-form-group label="Setor" label-for="setor_producao_id">
              <b-form-select
                id="setor_producao_id"
                v-model="filters.setor_producao_id"
                :options="setorOptions"
                @change="getPainel"
              />
            </b-form-group>
          </b-col>
          <b-col v-if="!restauranteProducaoEtapaUnica" md="4">
            <b-form-group label="Status" label-for="status_producao">
              <b-form-select
                id="status_producao"
                v-model="filters.status"
                :options="statusOptions"
                @change="getPainel"
              />
            </b-form-group>
          </b-col>
          <b-col :md="restauranteProducaoEtapaUnica ? 6 : 4">
            <div class="kds-toolbar">
              <label class="kds-toggle">
                <input v-model="autoRefresh" type="checkbox" />
                <span>Atualização automática</span>
              </label>
              <small>{{ lastRefreshLabel }}</small>
            </div>
          </b-col>
        </b-row>

        <div v-if="!itens.length" class="empty-state">
          Nenhum item na fila de produção para os filtros selecionados.
        </div>

        <div
          v-else
          :class="[
            'kds-board',
            { 'kds-board-etapa-unica': restauranteProducaoEtapaUnica },
          ]"
        >
          <div
            v-for="column in statusColumns"
            :key="column.status"
            class="kds-column"
          >
            <div :class="['kds-column-header', `kds-column-${column.status}`]">
              <strong>{{ column.label }}</strong>
              <span>{{ itensPorColuna(column).length }}</span>
            </div>

            <div
              v-if="!ticketsPorColuna(column).length"
              class="kds-column-empty"
            >
              Sem itens.
            </div>

            <div
              v-for="ticket in ticketsPorColuna(column)"
              :key="ticket.key"
              :class="[
                'producao-ticket',
                `producao-card-${ticket.status}`,
                {
                  'producao-card-atencao': ticket.atencao,
                  'producao-card-atrasado': ticket.atrasado,
                },
              ]"
            >
              <button
                type="button"
                class="producao-ticket-header"
                @click="toggleTicket(ticket)"
              >
                <div>
                  <strong>{{ itemMesaLabel(ticket.firstItem) }}</strong>
                  <span>{{ itemSetorLabel(ticket.firstItem) }}</span>
                </div>
                <span class="producao-ticket-summary">
                  {{ ticket.items.length }} item(ns)
                </span>
                <span class="kds-timer">
                  {{ tempoDecorridoLabel(ticket.firstItem) }}
                </span>
                <i
                  :class="[
                    'fas',
                    isTicketExpanded(ticket)
                      ? 'fa-chevron-up'
                      : 'fa-chevron-down',
                  ]"
                ></i>
              </button>

              <div class="producao-ticket-meta">
                <span>{{ itemTicketLabel(ticket.firstItem) }}</span>
                <span>{{ formatDate(ticket.firstItem.enviado_at) }}</span>
              </div>

              <div
                v-if="ticketNextStatus(ticket)"
                class="producao-ticket-actions"
              >
                <button
                  type="button"
                  class="btn btn-sm btn-light-dark font-weight-bold"
                  @click="
                    updateTicketGroupStatus(ticket, ticketNextStatus(ticket))
                  "
                >
                  {{ actionLabel(ticketNextStatus(ticket), true) }}
                  <i class="fas fa-arrow-right producao-action-arrow"></i>
                </button>
              </div>

              <div
                v-if="isTicketExpanded(ticket)"
                class="producao-ticket-items"
              >
                <div
                  v-for="grupo in ticket.comandas"
                  :key="grupo.key"
                  class="producao-comanda-group"
                >
                  <div class="producao-comanda-header">
                    <strong>{{ grupo.label }}</strong>
                    <span>{{ grupo.items.length }} item(ns)</span>
                  </div>

                  <div
                    v-for="item in grupo.items"
                    :key="item.id"
                    class="producao-ticket-item"
                  >
                    <div class="producao-produto">
                      <strong>{{ produtoDescricao(item) }}</strong>
                      <span>{{ quantidadeItem(item) }}</span>
                    </div>

                    <div
                      v-if="observacaoLinhas(item).length"
                      class="producao-observacao"
                    >
                      <span
                        v-for="(linha, linhaIndex) in observacaoLinhas(item)"
                        :key="linhaIndex"
                      >
                        {{ linha }}
                      </span>
                    </div>

                    <div class="producao-actions">
                      <button
                        v-if="nextStatus(item.status)"
                        type="button"
                        class="btn btn-sm btn-primary font-weight-bold"
                        @click="updateStatus(item, nextStatus(item.status))"
                      >
                        {{ actionLabel(nextStatus(item.status)) }}
                        <i class="fas fa-arrow-right producao-action-arrow"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { restauranteFeatureFlags, restauranteTempoLimites } from "../../config";
import { imprimirTicketsProducao } from "../../services/qzTrayPrinter";

export default {
  name: "app-restaurante-producao",
  props: {
    tipoProducao: {
      type: String,
      default: null,
    },
    titulo: {
      type: String,
      default: "Produção",
    },
    subtitulo: {
      type: String,
      default: "Cozinha e bar",
    },
    exibirMesasAtalho: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      itens: [],
      setores: [],
      autoRefresh: true,
      refreshInterval: null,
      impressaoInterval: null,
      imprimindoPendentes: false,
      impressaoErroNotificadoAt: null,
      lastRefreshAt: null,
      featureFlags: restauranteFeatureFlags,
      restauranteProducaoEtapaUnica: false,
      restauranteUsarApenasComandas: false,
      expandedTickets: {},
      filters: {
        setor_producao_id: null,
        status: null,
      },
    };
  },
  computed: {
    ...mapGetters(["itsblocked"]),
    statusColumns() {
      if (this.restauranteProducaoEtapaUnica) {
        return [{ status: "pronto", label: "Pronto" }];
      }

      return [
        { status: "pendente", label: "Pendente" },
        { status: "preparando", label: "Preparando" },
        { status: "pronto", label: "Pronto" },
      ];
    },
    statusOptions() {
      return [
        { value: null, text: "Pendentes, preparando e prontos" },
        { value: "pendente", text: "Pendente" },
        { value: "preparando", text: "Preparando" },
        { value: "pronto", text: "Pronto" },
      ];
    },
    setorOptions() {
      return [
        { value: null, text: "Todos" },
        ...this.setores
          .filter(
            (setor) => !this.tipoProducao || setor.tipo === this.tipoProducao
          )
          .map((setor) => ({
            value: setor.id,
            text: `${setor.nome} (${this.tipoLabel(setor.tipo)})`,
          })),
      ];
    },
    producaoMetrics() {
      const count = (status) =>
        this.itens.filter((item) => item.status === status).length;
      const atrasados = this.itens.filter((item) =>
        this.isItemAtrasado(item)
      ).length;

      return [
        { label: "Na fila", value: count("pendente") },
        { label: "Preparando", value: count("preparando") },
        { label: "Prontos", value: count("pronto") },
        { label: "Atrasados", value: atrasados },
      ];
    },
    lastRefreshLabel() {
      if (!this.lastRefreshAt) {
        return "aguardando primeira carga";
      }

      return `atualizado ${this.lastRefreshAt.toLocaleTimeString("pt-BR")}`;
    },
  },
  mounted() {
    if (this.itsblocked) {
      this.$router.push({ name: "assinatura" });
      return;
    }

    this.getInit();
    this.refreshInterval = setInterval(() => {
      if (this.autoRefresh) {
        this.getPainel(false);
      }
    }, 30000);
    this.impressaoInterval = setInterval(() => {
      if (this.autoRefresh) {
        this.imprimirPendentesProducao();
      }
    }, 10000);
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    if (this.impressaoInterval) {
      clearInterval(this.impressaoInterval);
    }
  },
  methods: {
    ...mapActions(["setLoading"]),
    async getInit() {
      this.setLoading(true);
      try {
        await this.getConfiguracoesRestaurante();
        await Promise.all([this.getSetores(), this.getPainel(false)]);
        await this.imprimirPendentesProducao();
      } finally {
        this.setLoading(false);
      }
    },
    async getConfiguracoesRestaurante() {
      try {
        const { data } = await this.$http.post("/configuracoesgerais/find");
        const configuracoes = Array.isArray(data) ? data[0] : data;

        this.restauranteProducaoEtapaUnica =
          configuracoes?.restaurante_producao_etapa_unica ?? false;
        this.restauranteUsarApenasComandas =
          configuracoes?.restaurante_usar_apenas_comandas ?? false;

        if (this.restauranteProducaoEtapaUnica) {
          this.filters.status = null;
        }
      } catch (error) {
        this.restauranteProducaoEtapaUnica = false;
        this.restauranteUsarApenasComandas = false;
      }
    },
    async getSetores() {
      try {
        const { data } = await this.$http.post(
          "/restaurante/setores-producao/select"
        );
        this.setores = data;
      } catch (error) {
        this.$toast.error("Não foi possível carregar os setores.");
      }
    },
    async getPainel(showLoading = true) {
      try {
        if (showLoading) {
          this.setLoading(true);
        }

        const payload = {};

        if (this.filters.setor_producao_id) {
          payload.setor_producao_id = this.filters.setor_producao_id;
        }

        if (this.filters.status) {
          payload.status = this.filters.status;
        }

        if (this.tipoProducao) {
          payload.tipo = this.tipoProducao;
        }

        const { data } = await this.$http.post(
          "/restaurante/producao/painel",
          payload
        );
        this.itens = data;
        this.lastRefreshAt = new Date();
        this.imprimirPendentesProducao();
      } catch (error) {
        this.$toast.error("Não foi possível carregar a produção.");
      } finally {
        if (showLoading) {
          this.setLoading(false);
        }
      }
    },
    async updateStatus(item, status) {
      try {
        this.setLoading(true);
        await this.$http.post("/restaurante/producao/update-status", {
          id: item.id,
          status,
        });
        this.$toast.success("Status atualizado!");
        await this.getPainel(false);
      } catch (error) {
        const message =
          error.response && error.response.data && error.response.data.msg
            ? error.response.data.msg
            : "Não foi possível atualizar o status.";
        this.$toast.error(message);
      } finally {
        this.setLoading(false);
      }
    },
    async imprimirPendentesProducao() {
      if (this.imprimindoPendentes) {
        return;
      }

      this.imprimindoPendentes = true;

      try {
        const payload = {
          limite: 20,
        };

        if (this.tipoProducao) {
          payload.tipo = this.tipoProducao;
        }

        if (this.filters.setor_producao_id) {
          payload.setor_producao_id = this.filters.setor_producao_id;
        }

        const { data } = await this.$http.post(
          "/restaurante/producao/impressao-pendente",
          payload
        );

        if (!Array.isArray(data) || !data.length) {
          return;
        }

        await imprimirTicketsProducao(data);

        await this.$http.post("/restaurante/producao/marcar-impresso", {
          ids: data.map((ticket) => ticket.id).filter(Boolean),
        });
      } catch (error) {
        this.notificarErroImpressao(error);
      } finally {
        this.imprimindoPendentes = false;
      }
    },
    notificarErroImpressao(error) {
      const now = Date.now();

      if (
        this.impressaoErroNotificadoAt &&
        now - this.impressaoErroNotificadoAt < 60000
      ) {
        return;
      }

      this.impressaoErroNotificadoAt = now;
      this.$toast.warning(
        error.message ||
          "Existem pedidos pendentes, mas não foi possível imprimir no QZ Tray."
      );
    },
    itensPorStatus(status) {
      return this.itens.filter((item) => item.status === status);
    },
    itensPorColuna(column) {
      if (this.restauranteProducaoEtapaUnica) {
        return this.itens;
      }

      return this.itensPorStatus(column.status);
    },
    ticketsPorColuna(column) {
      const tickets = {};

      this.itensPorColuna(column).forEach((item) => {
        const key = `${
          this.restauranteProducaoEtapaUnica ? "etapa-unica" : column.status
        }:${this.ticketKey(item)}`;

        if (!tickets[key]) {
          tickets[key] = {
            key,
            status: item.status,
            firstItem: item,
            items: [],
            atencao: false,
            atrasado: false,
          };
        }

        tickets[key].items.push(item);

        if (
          this.itemTimestamp(item) < this.itemTimestamp(tickets[key].firstItem)
        ) {
          tickets[key].firstItem = item;
        }

        tickets[key].atencao = tickets[key].atencao || this.isItemAtencao(item);
        tickets[key].atrasado =
          tickets[key].atrasado || this.isItemAtrasado(item);
      });

      return Object.values(tickets)
        .map((ticket) => ({
          ...ticket,
          status: this.ticketStatus(ticket.items),
          items: ticket.items.sort(
            (first, second) =>
              this.itemTimestamp(first) - this.itemTimestamp(second)
          ),
          comandas: this.groupTicketItemsByComanda(ticket.items),
        }))
        .sort(
          (first, second) =>
            this.itemTimestamp(first.firstItem) -
            this.itemTimestamp(second.firstItem)
        );
    },
    ticketStatus(items) {
      const itemEmPreparo = items.find((item) => item.status !== "pronto");

      return itemEmPreparo ? itemEmPreparo.status : "pronto";
    },
    ticketNextStatus(ticket) {
      if (this.restauranteProducaoEtapaUnica) {
        return ticket.items.some((item) => item.status !== "pronto")
          ? "pronto"
          : null;
      }

      return this.nextStatus(ticket.status);
    },
    nextStatus(status) {
      if (this.restauranteProducaoEtapaUnica) {
        return status === "pronto" ? null : "pronto";
      }

      const next = {
        pendente: "preparando",
        preparando: "pronto",
      };

      return next[status] || null;
    },
    ticketKey(item) {
      return [
        item.atendimento_id || "atendimento",
        item.mesa?.id || item.mesa_id || "sem-mesa",
        item.setor_producao_id || item.setor_producao?.id || "sem-setor",
        this.ticketBatchKey(item),
      ].join(":");
    },
    ticketBatchKey(item) {
      const value =
        item.ticket_key ||
        item.pedido_producao_id ||
        item.lote_producao_id ||
        item.enviado_at ||
        item.item?.enviado_producao_at ||
        item.created_at ||
        item.id;

      return String(value || item.id || "sem-envio")
        .replace("T", " ")
        .slice(0, 19);
    },
    itemComandaKey(item) {
      return (
        item.comanda?.id ||
        item.item?.comanda?.id ||
        item.comanda_id ||
        item.item?.comanda_id ||
        "sem-comanda"
      );
    },
    itemComandaLabel(item) {
      const comanda = item.comanda || item.item?.comanda || null;

      if (this.restauranteUsarApenasComandas && comanda?.tipo === "avulso") {
        return this.comandaModoLabel(comanda);
      }

      if (comanda?.tipo === "avulso" || (!comanda?.nome && comanda?.codigo)) {
        return `Avulsa ${comanda.codigo || comanda.id}`;
      }

      const nome =
        item.comanda?.nome ||
        item.item?.comanda?.nome ||
        item.item?.comanda_nome ||
        "";
      const numero =
        item.comanda?.numero ||
        item.item?.comanda?.numero ||
        item.comanda_numero ||
        "";

      if (nome) {
        return nome;
      }

      return numero ? `Comanda ${numero}` : "Sem comanda";
    },
    comandaModoLabel(comanda) {
      const nome = String(comanda?.nome || "").trim();
      const codigo = String(comanda?.codigo || "").trim();

      if (nome && codigo) {
        return `Comanda ${nome} (${codigo})`;
      }

      if (nome) {
        return `Comanda ${nome}`;
      }

      if (codigo) {
        return `Comanda ${codigo}`;
      }

      return comanda?.id ? `Comanda #${comanda.id}` : "Comanda";
    },
    groupTicketItemsByComanda(items) {
      const grupos = {};

      items.forEach((item) => {
        const key = this.itemComandaKey(item);

        if (!grupos[key]) {
          grupos[key] = {
            key,
            label: this.itemComandaLabel(item),
            items: [],
          };
        }

        grupos[key].items.push(item);
      });

      return Object.values(grupos);
    },
    isTicketExpanded(ticket) {
      return this.expandedTickets[ticket.key] !== false;
    },
    toggleTicket(ticket) {
      this.$set(
        this.expandedTickets,
        ticket.key,
        !this.isTicketExpanded(ticket)
      );
    },
    ticketItems(item) {
      const key = this.ticketKey(item);
      return this.itens.filter(
        (ticketItem) =>
          this.ticketKey(ticketItem) === key &&
          ticketItem.status === item.status
      );
    },
    async updateTicketGroupStatus(ticket, status) {
      try {
        this.setLoading(true);
        const itens = this.restauranteProducaoEtapaUnica
          ? ticket.items.filter((ticketItem) => ticketItem.status !== "pronto")
          : ticket.items;

        if (!itens.length) {
          return;
        }

        if (this.featureFlags.kdsAvancado) {
          try {
            await this.$http.post("/restaurante/producao/bump", {
              ticket_key: ticket.key,
              ids: itens.map((ticketItem) => ticketItem.id),
              status,
            });
          } catch (error) {
            await this.updateTicketStatusFallback(itens, status);
          }
        } else {
          await this.updateTicketStatusFallback(itens, status);
        }

        this.$toast.success("Ticket atualizado!");
        await this.getPainel(false);
      } catch (error) {
        const message =
          error.response && error.response.data && error.response.data.msg
            ? error.response.data.msg
            : "Não foi possível atualizar o ticket.";
        this.$toast.error(message);
      } finally {
        this.setLoading(false);
      }
    },
    async updateTicketStatus(item, status) {
      try {
        this.setLoading(true);
        const itens = this.ticketItems(item);

        if (this.featureFlags.kdsAvancado) {
          try {
            await this.$http.post("/restaurante/producao/bump", {
              ticket_key: this.ticketKey(item),
              ids: itens.map((ticketItem) => ticketItem.id),
              status,
            });
          } catch (error) {
            await this.updateTicketStatusFallback(itens, status);
          }
        } else {
          await this.updateTicketStatusFallback(itens, status);
        }

        this.$toast.success("Ticket atualizado!");
        await this.getPainel(false);
      } catch (error) {
        const message =
          error.response && error.response.data && error.response.data.msg
            ? error.response.data.msg
            : "Não foi possível atualizar o ticket.";
        this.$toast.error(message);
      } finally {
        this.setLoading(false);
      }
    },
    updateTicketStatusFallback(itens, status) {
      return Promise.all(
        itens.map((ticketItem) =>
          this.$http.post("/restaurante/producao/update-status", {
            id: ticketItem.id,
            status,
          })
        )
      );
    },
    itemMesaLabel(item) {
      if (item.mesa) {
        return `Mesa ${item.mesa.numero}`;
      }

      const comanda = item.item?.comanda || item.comanda;

      if (this.restauranteUsarApenasComandas && comanda?.tipo === "avulso") {
        return this.comandaModoLabel(comanda);
      }

      if (comanda?.tipo === "avulso" || comanda?.codigo) {
        return `Avulsa ${comanda.codigo || comanda.id}`;
      }

      return "Mesa não informada";
    },
    itemSetorLabel(item) {
      return item.setor_producao ? item.setor_producao.nome : "Sem setor";
    },
    itemTicketLabel(item) {
      const atendimento = item.atendimento_id
        ? `Atendimento #${item.atendimento_id}`
        : "Atendimento";

      return atendimento;
    },
    minutosDecorridos(item) {
      const value =
        item.enviado_at ||
        item.created_at ||
        item.updated_at ||
        item.item?.created_at;

      if (!value) {
        return 0;
      }

      const startedAt = new Date(value);
      const diff = Date.now() - startedAt.getTime();
      return Number.isFinite(diff) ? Math.max(diff / 60000, 0) : 0;
    },
    tempoDecorridoLabel(item) {
      const minutos = Math.floor(this.minutosDecorridos(item));

      if (minutos < 60) {
        return `${minutos} min`;
      }

      const horas = Math.floor(minutos / 60);
      const resto = minutos % 60;
      return `${horas}h ${resto}m`;
    },
    isItemAtencao(item) {
      return (
        this.minutosDecorridos(item) >=
        restauranteTempoLimites.producaoAtencaoMinutos
      );
    },
    isItemAtrasado(item) {
      return (
        this.minutosDecorridos(item) >=
        restauranteTempoLimites.producaoAtrasoMinutos
      );
    },
    itemTimestamp(item) {
      const value =
        item.enviado_at ||
        item.created_at ||
        item.updated_at ||
        item.item?.created_at;

      if (!value) {
        return Date.now();
      }

      const timestamp = new Date(value).getTime();
      return Number.isFinite(timestamp) ? timestamp : Date.now();
    },
    produtoDescricao(item) {
      return item.item && item.item.produto
        ? item.item.produto.descricao
        : `Produto #${item.item ? item.item.produto_id : "-"}`;
    },
    quantidadeItem(item) {
      if (!item.item) {
        return "-";
      }

      const unidade =
        item.item.produto &&
        item.item.produto.unidade_medida &&
        item.item.produto.unidade_medida.sigla
          ? item.item.produto.unidade_medida.sigla
          : "";

      return `${Number(item.item.quantidade || 0).toLocaleString(
        "pt-BR"
      )} ${unidade}`;
    },
    observacaoLinhas(item) {
      const observacao = String(item.observacao || "")
        .replace(/\s*\|\s*\[pedido_online[^\]]+\]/g, "")
        .replace(/\[pedido_online[^\]]+\]/g, "")
        .trim();

      if (!observacao) {
        return [];
      }

      return observacao
        .split(/\s*\|\s*/)
        .map((linha) => linha.trim())
        .filter(Boolean);
    },
    actionLabel(status, ticket = false) {
      const labels = {
        preparando: "Preparar",
        pronto: "Pronto",
      };
      const label = labels[status] || this.statusLabel(status);

      return ticket ? `${label} ticket` : label;
    },
    statusLabel(status) {
      const labels = {
        pendente: "Pendente",
        preparando: "Preparando",
        pronto: "Pronto",
      };

      return labels[status] || status;
    },
    statusBadgeClass(status) {
      const classes = {
        pendente: "label label-lg label-inline label-light-warning",
        preparando: "label label-lg label-inline label-light-primary",
        pronto: "label label-lg label-inline label-light-info",
      };

      return classes[status] || "label label-lg label-inline label-light";
    },
    tipoLabel(tipo) {
      const labels = {
        cozinha: "Cozinha",
        bar: "Bar",
        outro: "Outro",
      };

      return labels[tipo] || tipo;
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }

      return new Date(value).toLocaleString("pt-BR");
    },
  },
};
</script>

<style scoped>
.restaurante-producao .card-body {
  min-height: 420px;
}

.kds-resumo {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 18px;
}

.kds-metric {
  background: #fff;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 14px;
}

.kds-metric span {
  color: #7e8299;
  display: block;
  font-weight: 700;
}

.kds-metric strong {
  color: #181c32;
  display: block;
  font-size: 1.6rem;
  line-height: 1.2;
  margin-top: 4px;
}

.kds-toolbar {
  align-items: center;
  background: #f3f6f9;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  min-height: 46px;
  padding: 0 12px;
}

.kds-toolbar small {
  color: #7e8299;
  font-weight: 700;
}

.kds-toggle {
  align-items: center;
  color: #3f4254;
  display: flex;
  gap: 8px;
  font-weight: 700;
  margin: 0;
}

.empty-state {
  background: #f3f6f9;
  border-radius: 6px;
  color: #7e8299;
  font-weight: 600;
  padding: 24px;
  text-align: center;
}

.kds-board {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(3, minmax(240px, 1fr));
  overflow-x: auto;
  padding-bottom: 4px;
}

.kds-board-etapa-unica {
  grid-template-columns: minmax(280px, 1fr);
}

.kds-column {
  background: #f7f8fb;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  min-height: 560px;
  padding: 10px;
}

.kds-column-header {
  align-items: center;
  background: #fff;
  border-left: 5px solid #ffa800;
  border-radius: 6px;
  color: #181c32;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  min-height: 46px;
  padding: 10px 12px;
}

.kds-column-header strong,
.kds-column-header span {
  font-weight: 800;
}

.kds-column-header span {
  background: #f3f6f9;
  border-radius: 999px;
  color: #3f4254;
  min-width: 28px;
  padding: 3px 8px;
  text-align: center;
}

.kds-column-preparando {
  border-left-color: #3699ff;
}

.kds-column-pronto {
  border-left-color: #8950fc;
}

.kds-column-empty {
  color: #7e8299;
  font-weight: 700;
  padding: 18px 8px;
  text-align: center;
}

.producao-ticket {
  background: #fff;
  border: 1px solid #ebedf3;
  border-left: 5px solid #ffa800;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
  padding: 14px;
}

.producao-card-preparando {
  border-left-color: #3699ff;
}

.producao-card-pronto {
  border-left-color: #8950fc;
}

.producao-card-atencao {
  box-shadow: 0 0 0 3px rgba(255, 168, 0, 0.18);
}

.producao-card-atrasado {
  box-shadow: 0 0 0 3px rgba(246, 78, 96, 0.2);
}

.producao-ticket-header {
  align-items: flex-start;
  background: transparent;
  border: 0;
  color: #181c32;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 0;
  text-align: left;
  width: 100%;
}

.producao-ticket-header div {
  display: flex;
  flex-direction: column;
}

.producao-ticket-header strong,
.producao-produto strong {
  color: #181c32;
  font-size: 1rem;
  font-weight: 700;
}

.producao-ticket-header span,
.producao-produto span,
.producao-ticket-meta span {
  color: #7e8299;
  font-weight: 600;
}

.producao-ticket-summary {
  background: #f3f6f9;
  border-radius: 999px;
  color: #3f4254 !important;
  padding: 4px 8px;
  white-space: nowrap;
}

.kds-timer {
  background: #f3f6f9;
  border-radius: 999px;
  color: #181c32 !important;
  font-weight: 800 !important;
  padding: 4px 8px;
  white-space: nowrap;
}

.producao-ticket-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 10px;
}

.producao-ticket-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.producao-ticket-items {
  border-top: 1px solid #ebedf3;
  display: grid;
  gap: 10px;
  margin-top: 14px;
  padding-top: 12px;
}

.producao-comanda-group {
  display: grid;
  gap: 8px;
}

.producao-comanda-header {
  align-items: center;
  background: #f3f6f9;
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
}

.producao-comanda-header strong {
  color: #181c32;
  font-size: 0.92rem;
  font-weight: 800;
}

.producao-comanda-header span {
  color: #7e8299;
  font-weight: 700;
  white-space: nowrap;
}

.producao-ticket-item {
  background: #f7f8fb;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 12px;
}

.producao-produto {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.producao-observacao {
  background: #fff;
  border-radius: 6px;
  color: #3f4254;
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-weight: 600;
  line-height: 1.35;
  margin: 10px 0 0;
  padding: 10px;
}

.producao-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.producao-action-arrow {
  margin-left: 8px;
  transform: translateX(0);
  transition: transform 0.15s ease;
}

.producao-ticket-actions .btn:hover .producao-action-arrow,
.producao-actions .btn:hover .producao-action-arrow {
  transform: translateX(3px);
}

@media (max-width: 991.98px) {
  .kds-resumo {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .kds-board {
    grid-template-columns: repeat(3, minmax(260px, 1fr));
  }

  .kds-board-etapa-unica {
    grid-template-columns: minmax(260px, 1fr);
  }
}

@media (max-width: 575.98px) {
  .kds-resumo {
    grid-template-columns: 1fr;
  }

  .kds-toolbar {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
    justify-content: center;
    padding: 8px 12px;
  }
}
</style>
